import type React from "react";
import { useEffect, useState } from "react";
import Column from "@/atoms-react/column/Column.react";
import { useUser } from "@/features/user/hooks/use-user";
import type { Document } from "@/features/user/document/types";
import { useDocument } from "@/features/user/document/hooks/use-document";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { DragAndDrop } from "@/views/identity/components/drag-drop/index.react";
import { usei18n } from "@/features/utils/hooks/use-i18n";

type DocumentsProps = {
  handleNewDoc: (docs: Document[]) => void;
  documentsType?: number;
} & React.ComponentPropsWithoutRef<"div">;
export const DocumentsLevel = ({ ...props }: DocumentsProps) => {
  const { t } = useTranslation(["pass"]);
  const { getLanguage } = usei18n();

  const { useUserProfile } = useUser();
  const { data: user } = useUserProfile();
  const [params] = useSearchParams();
  const { DocumentsDetailsQuery, UploadDocumentMutation } = useDocument();
  const { data: documents, refetch } = DocumentsDetailsQuery(user?.naturalPerson?.UUID ?? null);
  const { mutateAsync: uploadDocument } = UploadDocumentMutation();
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [newDocs, setNewDocs] = useState<Document[] | []>([]);

  useEffect(() => {
    if (selectedDocument === null) {
      const previewUUID = params.get("preview");
      const found = documents?.find((doc) => doc.uuid === previewUUID);
      if (found) {
        setSelectedDocument(found);
      }
    }
  }, [documents, params]);

  const onUpload = async (files: FileList | null) => {
    if (!files || loading) {
      return;
    }
    setLoading(true);
    const toBase64 = (file: File) =>
      new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve((reader.result as string).split(",")[1]);
        reader.onerror = reject;
      });
    const documentCountBeforeUpload = documents?.length || 0;
    const documentCountBeforeUploadFiles = documents;
    const base64 = await toBase64(files[0]);
    try {
      await uploadDocument({
        ownerUUID: user?.naturalPerson?.UUID ?? "",
        language: getLanguage(),
        document_type_id: props.documentsType,
        document: {
          base64Doc: base64,
          fileFormat: files[0].type,
          fileName: files[0].name
        }
      });
      const { data: newDocuments } = await refetch();
      if ((newDocuments?.length || 0) > documentCountBeforeUpload) {
        const newDoc = newDocuments?.filter(
          (objet) => !documentCountBeforeUploadFiles?.some((item) => item.Number === objet.Number)
        );
        setNewDocs([...newDocs, ...(newDoc as Document[])]);
        props.handleNewDoc([...newDocs, ...(newDoc as Document[])]);
        toast.success(t("aliasDocumentsPage.successfully_added"));
      } else {
        toast.error(t("aliasDocumentsPage.error_import"));
      }
      setLoading(false);
    } catch (_) {
      setLoading(false);

      toast.error(t("aliasDocumentsPage.error_import"));
    }
  };

  return (
    <Column {...props} className="flex flex-col">
      <div className="-mx-8 flex h-full flex-1 flex-col overflow-hidden">
        <div className="flex-1 border-slate-50 md:flex">
          {/* Documents list, drag & drop (left part) */}
          <div className="w-full flex-1 border-slate-50 p-8 md:flex-1 md:border-r-2">
            <DragAndDrop className="2xl:h-52" onValidated={onUpload} isLoading={loading} />
          </div>
          <div className="w-full md:hidden"></div>
          {/* Preview (right part) */}
        </div>
      </div>
    </Column>
  );
};
